// Dutch string catalog for JustEasySEO. Keep keys stable & namespaced.
// This file is the SINGLE presentation-mapping layer. Underlying DB
// enum values (PROPOSED, HYPOTHESIS, DIRECT_BUSINESS_COMPETITOR, …)
// remain unchanged; only labels shown to the user come from here.
const nl = {
  app: {
    name: "JustEasySEO",
    subtitle: "SEO zonder gedoe",
    tagline: "SEO zonder gedoe. SEO, advertenties en social media. Simpel geregeld.",
    product_underline: "Je AI-marketingdirecteur voor overzicht, advies en uitvoering met jouw akkoord.",
    ghost_active: "ADVIESMODUS ACTIEF",
    ghost_active_hint: "We analyseren en adviseren. In Ghost Mode wijzigen of publiceren we niets.",
    demo_badge: "DEMO",
    demo_hint: "U kijkt naar testgegevens — geen echte cijfers.",
  },
  common: {
    save: "Opslaan",
    cancel: "Annuleren",
    edit: "Wijzigen",
    delete: "Verwijderen",
    loading: "Even geduld, ik haal uw gegevens op.",
    error_generic: "Er ging iets mis. Probeer het opnieuw. Blijft het probleem? Neem contact op met support.",
    tech_code: "Technische code",
    optional: "optioneel",
    yes: "Ja",
    no: "Nee",
    close: "Sluiten",
    back: "Terug",
    open: "Open",
    hypothesis_label: "Vermoeden",
    view_evidence: "Bekijk bewijs en details",
    hide_evidence: "Verberg details",
  },
  auth: {
    login_title: "Log in bij JustEasySEO",
    signup_title: "Maak uw JustEasySEO-account",
    email: "E-mailadres",
    password: "Wachtwoord",
    name: "Uw naam",
    organization_name: "Naam van uw bedrijf",
    submit_login: "Inloggen",
    submit_signup: "Account aanmaken",
    google_continue: "Doorgaan met Google",
    google_soon: "Inloggen met Google komt eraan",
    to_signup: "Nog geen account? Registreer",
    to_login: "Al een account? Log in",
    google_notice_short:
      "Na het aanmelden kunt u uw advertenties en website eenvoudig koppelen. U kiest zelf waarvoor u toestemming geeft.",
    forgot: "Wachtwoord vergeten?",
  },
  // Navigation labels — new plain-Dutch names.
  nav: {
    command_center: "Overzicht",
    pagina_focus: "Pagina-focus",
    merkstem: "Merkstem",
    adviezen: "Adviezen",
    effect: "Effect",
    ceo_dashboard: "Ondernemersdashboard",
    seo: "SEO",
    social: "Social Suite",
    publish_queue: "Publish Queue",
    integrations: "Integraties",
    site_audit: "Site-analyse",
    intake: "Uw bedrijf",
    privacy_vault: "Gegevens en koppelingen",
    business_brain: "Bedrijfskennis",
    ghost_ledger: "Adviezen en besluiten",
    weekly_report: "Weekrapport",
    weekplan: "Weekplan",
    vlogs: "Vlogs",
    berichten: "Berichten",
    wallet: "Marketingbudget",
    market_radar: "Marktoverzicht",
    connections: "Verbindingen",
    logout: "Uitloggen",
  },
  intake: {
    title: "Uw bedrijf",
    subtitle:
      "Voordat JustEasySEO iets analyseert, wil ik uw bedrijf begrijpen én uw grenzen kennen.",
    progress: "Voortgang",
    placeholder: "Typ uw antwoord…",
    send: "Verstuur",
    skip: "Overslaan",
    finish: "Vragenlijst afronden",
    complete_title: "Vragenlijst afgerond",
    complete_body:
      "Ik bouw uw Bedrijfskennis nu op. Nog één stap: uw Gegevens en koppelingen instellen.",
    to_privacy: "Naar Gegevens en koppelingen",
    typing: "JustEasySEO denkt na…",
  },
  vault: {
    title: "Gegevens en koppelingen",
    subtitle:
      "U bepaalt precies welke gegevens JustEasySEO mag zien en waarover JustEasySEO mag adviseren.",
    permission_labels: {
      PRIVATE: "PRIVÉ",
      READ: "LEZEN",
      ADVISE: "ADVISEREN",
      ACT: "UITVOEREN",
    },
    permission_desc: {
      PRIVATE: "JustEasySEO heeft geen toegang.",
      READ: "JustEasySEO mag deze zakelijke gegevens lezen en analyseren.",
      ADVISE: "JustEasySEO mag daarnaast concrete verbeteringen voorstellen.",
      ACT: "JustEasySEO mag binnen door u ingestelde grenzen acties uitvoeren.",
    },
    act_disabled: "Beschikbaar in een latere versie",
    not_available: "Nog niet beschikbaar — koppeling voorbereid",
    connected: "Gekoppeld",
    not_connected: "Niet gekoppeld",
    can_see: "Wat JustEasySEO kan zien",
    cannot_see: "Wat JustEasySEO nooit ziet",
    why: "Waarom deze gegevens",
    never_collects: "JustEasySEO verzamelt nooit:",
  },
  brain: {
    title: "Bedrijfskennis",
    subtitle: "Het gestructureerde geheugen van JustEasySEO over uw bedrijf.",
    profile_tab: "Bedrijfsprofiel",
    knowledge_tab: "Kennisitems",
    competitors_tab: "Concurrenten",
    search_ph: "Zoek in uw bedrijfskennis…",
    filter_all: "ALLE",
    empty: "Nog geen bedrijfskennis vastgelegd. Vul eerst de vragenlijst 'Uw bedrijf' in.",
    empty_no_data: "Nog geen gegevens gekoppeld. Ga naar Gegevens en koppelingen om te starten.",
    empty_filter: "Geen resultaten binnen dit filter.",
    invalidate: "Ongeldig markeren",
    reactivate: "Weer actief maken",
    confidence: "Hoe zeker",
    tags: "Labels",
    assumptions: "Aannames",
    col_type: "Soort",
    col_source: "Bron",
    col_tier: "Kwaliteit bron",
    col_confidence: "Hoe zeker",
    col_content: "Inhoud",
    col_updated: "Bijgewerkt",
    edit_company_basics: "Bedrijfsgegevens wijzigen",
    competitors: {
      add_title: "Concurrent toevoegen",
      name: "Naam",
      name_ph: "Bijv. Vloerspecialist Amsterdam",
      domain: "Website",
      domain_ph: "voorbeeld.nl",
      classification: "Type concurrent",
      notes: "Notities",
      notes_ph: "Waarom beschouwt u dit als concurrent? (optioneel)",
      add_btn: "Toevoegen",
      unknown_btn: "Weet ik nog niet",
      unknown_hint: "Sla een naam op zonder type — u kunt later aanvullen.",
      empty: "Nog geen concurrenten toegevoegd. Voeg de 3–5 belangrijkste toe.",
      saved: "Opgeslagen.",
      error_generic: "Er ging iets mis bij het opslaan.",
      exclude_confirm:
        "Weet u zeker dat u deze concurrent wilt uitsluiten? Deze keuze overschrijft toekomstige AI-suggesties.",
      change_class: "Type wijzigen",
      seo_note: "Deelt zoekwoorden, maar niet noodzakelijk uw directe zakelijke concurrent.",
      history_toggle: "Toon geschiedenis",
      history_hide: "Verberg geschiedenis",
      history_empty: "Nog geen wijzigingen vastgelegd.",
      status_confirmed: "BEVESTIGD",
      status_excluded: "UITGESLOTEN",
      status_unknown: "WEET IK NIET",
      status_unconfirmed: "AI-SUGGESTIE",
      snapshot_btn: "Website vastleggen",
      snapshot_view: "Eerdere controles bekijken",
      snapshot_no_domain: "Voeg eerst een website toe om snapshots te maken.",
      type: {
        DIRECT_BUSINESS_COMPETITOR: "Directe concurrent",
        PARTIAL_COMPETITOR: "Gedeeltelijke concurrent",
        SEO_COMPETITOR: "Concurrent in zoekresultaten",
        ADVERTISING_COMPETITOR: "Concurrent in advertenties",
        CONTENT_AUTHORITY_COMPETITOR: "Concurrent met informatieve inhoud",
        NOT_A_COMPETITOR: "Geen concurrent",
      },
    },
  },
  dashboard: {
    title: "Overzicht",
    advies_mode_hint: "JustEasySEO stelt verbeteringen voor. U beslist; JustEasySEO voert nu niets automatisch uit.",
    kpi_health: "App-status",
    kpi_health_empty: "Nog niet beschikbaar",
    kpi_health_hint: "Ga naar Gegevens en koppelingen om Google Ads te koppelen",
    kpi_budget: "Maandelijks budget",
    kpi_budget_empty: "Nog niet ingesteld",
    kpi_opportunity: "#1 Kans",
    kpi_opportunity_empty: "Nog geen gegevens",
    kpi_waste: "Mogelijk verspild",
    kpi_waste_empty: "Nog geen gegevens",
    would_do_today: "Wat JustEasySEO vandaag zou doen",
    would_do_empty:
      "Zodra u gegevens koppelt, laat ik hier concrete verbeteringen zien.",
    ghost_decisions: "Recente besluiten",
    ghost_decisions_empty: "Nog geen besluiten om te tonen — JustEasySEO wordt actief na het koppelen van uw gegevens.",
    pacing_ok: "Marketingbudget loopt binnen bereik",
    pacing_let_op: "Op koers naar {projected} van {cap} ({pct}%) — nog {days} dagen",
    pacing_warn: "Op koers naar {projected} van {cap} ({pct}%) — kijk mee",
    pacing_over: "Prognose {projected} boven cap {cap} — direct bijsturen",
    pacing_not_configured: "Marketingbudget nog niet ingesteld →",
    view_decision: "Bekijk dit advies",
    hide_decision: "Verberg dit advies",
    competitor_changes_title: "Concurrent-veranderingen deze week",
    competitor_changes_empty:
      "Nog geen veranderingen gezien. Laat JustEasySEO regelmatig een kopie van hun websites maken om veranderingen te volgen.",
    competitor_changes_view_all: "Alle concurrenten bekijken",
    confirmed_competitors_title: "Uw concurrenten",
    confirmed_competitors_empty:
      "Nog geen bevestigde concurrenten. Beheer uw lijst in Bedrijfskennis.",
    confirmed_competitors_manage: "Beheer in Bedrijfskennis",
    confirmed_competitors_more: "+{n} meer",
    competitor_badge_direct: "Directe concurrent",
    competitor_badge_partial: "Gedeeltelijke concurrent",
    competitor_badge_excluded: "Uitgesloten",
    evidence_view_in_brain: "Bekijk in Bedrijfskennis",
    evidence_tier_a: "Sterke bron",
    evidence_tier_b: "Redelijke bron",
    evidence_tier_c: "Zwakke bron",
    evidence_tier_d: "Indicatie",
    provenance_from_intake: "Uit vragenlijst",
    provenance_manual_edited: "Handmatig gewijzigd op {date}",
    ask_ph: "Vraag JustEasySEO iets over uw marketing…",
    ask_send: "Vraag stellen",
    ask_placeholder_response: "JustEasySEO denkt na — een moment geduld.",
    ask_title: "Vraag het JustEasySEO",
    next_step_label: "Volgende stap",
    intake_incomplete_banner: "Rond eerst 'Uw bedrijf' af om JustEasySEO te activeren.",
    open_full_ledger: "Bekijk alle adviezen en besluiten",
    open_weekly_report: "Weekrapport bekijken",
    open_weekly_report_hint: "Samenvatting van deze week — top-aandachtspunten, kansen en prioriteiten.",
    kpi_wallet_label: "Budget",
    kpi_wallet_cap: "per maand",
    kpi_wallet_not_set: "Niet ingesteld",
    kpi_pacing: "Uitgavenritme",
    kpi_pacing_days: "{n} dagen resterend",
    kpi_opportunities: "Kansen",
    kpi_opportunities_hint: "open",
    kpi_today: "Voorstellen vandaag",
    kpi_today_hint: "adviezen",
    kpi_all: "Alles",
  },
  timeline: {
    title: "Recente besluiten",
    empty: "Nog geen besluiten om te tonen.",
    tooltip_evidence: "Advies (bewijs)",
    tooltip_confidence: "Zekerheid",
    tooltip_range: "Verwachte range",
    view_link: "Bekijk dit advies",
  },
  ledger: {
    title: "Adviezen en besluiten",
    subtitle: "Alle voorstellen die JustEasySEO in Adviesmodus heeft geformuleerd — nooit automatisch uitgevoerd, altijd terug te vinden.",
    perf_title: "Prestaties",
    perf_disclaimer: "Adviesmodus voert géén acties uit; de getoonde impact is een vermoeden op basis van de gemeten periode.",
    perf_analyzed: "Gebeurtenissen geanalyseerd",
    perf_opps: "Open kansen",
    perf_decisions: "Besluiten",
    perf_validated: "Later bevestigd",
    perf_invalidated: "Later weerlegd",
    perf_inconclusive: "Nog geen duidelijke conclusie",
    perf_hypothetical_range: "Mogelijk effect",
    filter_channel: "Kanaal",
    filter_outcome: "Uitkomst",
    filter_status: "Status",
    filter_all: "Alle",
    empty: "Geen adviezen binnen deze filters.",
    empty_no_data: "Nog geen gegevens gekoppeld. Ga naar Gegevens en koppelingen om te starten.",
    step_what_i_see: "Wat ik zie",
    step_why: "Waarom dit belangrijk is",
    step_advise: "Wat ik adviseer",
    step_cost_return: "Wat het waarschijnlijk kost of oplevert",
    step_confidence: "Hoe zeker ik ben",
    deep_dive: "Bekijk bewijs en details",
    hide_deep_dive: "Verberg details",
    dd_evidence: "Bewijs",
    dd_assumptions: "Aannames",
    dd_risk: "Risico",
    dd_conflicting: "Tegenstrijdige signalen",
    dd_conflicting_empty: "Geen tegenstrijdige signalen waargenomen.",
    dd_score_breakdown: "Onderbouwing score",
    hypothesis_label: "Vermoeden",
    ghost_only_note: "Dit is een voorstel in Adviesmodus — JustEasySEO voert dit niet automatisch uit.",
    // Date-range filter
    date_range_label: "Periode",
    date_preset_today: "Vandaag",
    date_preset_week: "Deze week",
    date_preset_month: "Deze maand",
    date_preset_custom: "Aangepast",
    date_from: "Van",
    date_to: "Tot",
    date_reset: "Filter wissen",
    date_active_range: "Getoond: {range}",
    // Actions
    record_reality: "Wat heeft u gedaan?",
    evaluate_now: "Evalueer nu",
    evaluate_result: "Evaluatie: {outcome}",
    status_all: "Alle statussen",
    // Table columns
    col_time: "Tijd",
    col_type: "Type",
    col_channel: "Kanaal",
    col_impact: "Verwachte impact",
    col_status: "Status",
  },
  weekly: {
    title: "Weekrapport",
    history_title: "Eerdere weekrapporten",
    history_empty: "Nog geen opgeslagen weekrapporten.",
    generate_snapshot: "Weekrapport nu vastleggen",
    unseen_badge: "Nieuw",
    live_label: "Actueel",
    snapshot_seen: "Gelezen",
    snapshot_unseen: "Ongelezen",
    mark_seen: "Markeer als gelezen",
    section_summary: "Deze week in het kort",
    section_budget: "Budget en besteding",
    section_attention: "Belangrijkste aandachtspunten",
    section_opportunities: "Belangrijkste kansen",
    section_priorities: "Prioriteiten voor komende week",
    empty_attention_no_data: "Onvoldoende gegevens om aandachtspunten te tonen. Ga naar Gegevens en koppelingen om te starten.",
    empty_opportunities_no_data: "Onvoldoende gegevens om kansen te tonen. Ga naar Gegevens en koppelingen om te starten.",
    empty_attention_none: "Geen urgente aandachtspunten deze week.",
    empty_opportunities_none: "Nog geen concrete kansen deze week.",
    empty_priorities: "Nog geen prioriteiten — deze verschijnen zodra JustEasySEO gegevens heeft om op te bouwen.",
    kpi_spend: "Uitgegeven",
    kpi_clicks: "Klikken",
    kpi_impressions: "Vertoningen",
    kpi_conversions: "Conversies",
    kpi_cap: "Maandcap",
    kpi_spent: "Besteed",
    kpi_remaining: "Resterend",
    kpi_projection: "Prognose einde maand",
    confidence_label: "Hoe zeker",
    generated_at: "Gegenereerd",
    ghost_note: "Adviesmodus — observatie en advies, geen automatische uitvoering.",
  },
  wallet: {
    title: "Marketingbudget",
    subtitle: "Stel uw maandbudget en verdeling per kanaal in. JustEasySEO blijft in Adviesmodus — dit budget stuurt observaties en advies, geen automatische uitgaven.",
    monthly_cap: "Maandcap (€)",
    mode_label: "Verdeelmodus",
    mode_fixed: "Vaste verdeling",
    mode_flexible: "Flexibel",
    mode_fixed_hint: "Som van kanaalbedragen moet exact gelijk zijn aan de maandcap.",
    mode_flexible_hint: "Som van kanaalbedragen mag de maandcap niet overschrijden; het ongebruikte deel is flexibel.",
    save: "Opslaan",
    saved_ok: "Marketingbudget opgeslagen.",
    stat_cap: "Maandcap",
    stat_spent: "Besteed (deze maand)",
    stat_remaining: "Resterend",
    stat_mode: "Modus",
  },
  market: {
    title: "Marktoverzicht",
    subtitle: "Marktsignalen die relevant zijn voor uw bedrijf, op basis van uw Bedrijfskennis.",
    refresh: "Handmatig verversen",
    refreshing: "Verversen…",
    last_updated: "Laatst bijgewerkt",
    empty_no_profile: "Vul eerst de vragenlijst 'Uw bedrijf' in zodat JustEasySEO weet welke marktsignalen voor u relevant zijn.",
    empty_unreachable: "Openbare bronnen zijn nu niet bereikbaar. Probeer het later opnieuw.",
    empty_no_relevant: "Geen bedrijfsrelevante artikelen gevonden in de recente publieke nieuwsbronnen.",
    empty_default: "Er zijn nog geen bedrijfsrelevante marktsignalen. Klik op 'Handmatig verversen' voor een nieuwe scan.",
    fallback_notice: "Openbare bronnen tijdelijk niet bereikbaar — samenvatting op basis van algemene marktkennis.",
    rate_limited: "Verversen kan maximaal 1× per 24 uur — u ziet nu de meest recente beschikbare signalen.",
    what_happened: "Wat er speelt",
    why_matters: "Waarom dit voor u relevant is",
    advice: "Advies",
    horizon: "Horizon",
    relevance: "Relevantie voor uw bedrijf",
    source: "Bron",
    confidence: "Hoe zeker",
    ot_opportunity: "Kans",
    ot_threat: "Bedreiging",
    ot_neutral: "Neutraal",
  },
  competitors: {
    snapshots_title: "{name} — snapshots",
    snapshots_no_domain: "Geen website bekend — voeg eerst een adres toe in Bedrijfskennis.",
    snapshots_back: "Terug naar Bedrijfskennis",
    snapshots_run: "Snapshot nu maken",
    snapshots_running: "Snapshot maken…",
    snapshots_empty: "Nog geen snapshots. Klik op 'Snapshot nu maken' om te starten.",
    snapshots_empty_hint: "Maak minimaal twee snapshots om verschillen te vergelijken.",
    snapshots_changes_empty: "Nog geen veranderingen gezien voor deze concurrent. Maak minimaal twee snapshots om verschillen te vergelijken.",
    snapshots_changelist_title: "Tijdlijn van veranderingen",
    change_ok: "Snapshot gemaakt — {n} verandering(en) gevonden.",
    change_none: "Snapshot gemaakt — geen wijzigingen ten opzichte van de vorige snapshot.",
    view_original: "Bekijk originele pagina",
    section_status: "Status",
    section_meta: "Beschrijving",
    section_sections: "Secties (h2)",
    section_cta: "Knoppen (CTA's)",
  },
  // ------------------------------------------------------------------
  // Enum → label maps. These translate DB codes to plain Dutch labels
  // for display only. DB values stay untouched.
  // ------------------------------------------------------------------
  enum: {
    outcome: {
      PROPOSED: "Voorgesteld",
      ACTIVE: "Actief",
      VALIDATED_LATER: "Later bevestigd",
      INVALIDATED_LATER: "Later weerlegd",
      INCONCLUSIVE: "Nog geen duidelijke conclusie",
      APPROVED_CONCEPTUALLY: "Conceptueel goedgekeurd",
      REJECTED_BY_USER: "Afgewezen door u",
    },
    status: {
      ACTIVE: "Actief",
      RECONSIDER: "Nog eens bekijken",
      EXPIRED: "Verlopen",
      WITHDRAWN: "Ingetrokken",
    },
    knowledge_type: {
      FACT: "Feit",
      OBSERVATION: "Waarneming",
      HYPOTHESIS: "Vermoeden",
      PREDICTION: "Verwachting",
      RECOMMENDATION: "Advies",
      LEARNED_PATTERN: "Herkend patroon",
      ALL: "Alle",
    },
    evidence: {
      MEASURED: "Gemeten",
      EXPERIMENTALLY_SUPPORTED: "Ondersteund door experiment",
      MODELLED_ESTIMATE: "geschat met een rekenmodel",
      DIRECTIONAL_ESTIMATE: "een globale inschatting",
      UNKNOWN: "nog onbekend",
    },
    comparison_verdict: {
      STRONGER: "Sterker",
      EQUAL: "Gelijk",
      WEAKER: "Zwakker",
    },
    comparison_source: {
      BUSINESS_PROFILE: "Uw bedrijfsprofiel",
      COMPETITOR_SNAPSHOT: "Website-opname",
      KNOWLEDGE_ITEM: "Bedrijfskennis",
    },
    tier: {
      TIER_A: "Sterke bron",
      TIER_B: "Redelijke bron",
      TIER_C: "Zwakke bron",
      TIER_D: "Indicatie",
    },
    reliability: {
      PROVEN: "Bewezen voor uw bedrijf",
      BUILDING_EVIDENCE: "Nog aan het opbouwen",
      INSUFFICIENT_DATA: "Onvoldoende gegevens",
    },
    action_type: {
      TEST_MATCH_TYPE_CHANGE: "Gerichtere zoekwoorden testen",
      ADD_NEGATIVE_KEYWORDS: "Sluit onpassende zoekopdrachten uit",
      NEGATIVE_KEYWORD_ADD: "Ongewenste zoekopdrachten uitsluiten",
      REVIEW_CAMPAIGN_CPA: "Bekijk uw kosten per aanvraag",
      REVIEW_LOW_CTR_AD_GROUP: "Bekijk advertentiegroep met lage doorklikratio",
      GHOST: "Adviesmodus",
      MEASURE_ONLY: "Alleen meten",
    },
    source: {
      intake: "Door u ingevuld",
      user_input: "Door u ingevuld",
      manual_edit: "Door u aangepast",
      system: "Automatisch door JustEasySEO",
      google_ads: "Google Ads",
      ga4: "Google Analytics",
      gsc: "Search Console",
      market_radar: "Marktoverzicht",
      competitor_radar: "Concurrenten",
      user_feedback: "Uw terugkoppeling",
    },
    sector: {
      ECOMMERCE: "Webwinkel",
      "E-COMMERCE": "Webwinkel",
      RETAIL: "Winkels",
      "IT-SAAS": "Software en IT-diensten",
      SAAS: "Software en IT-diensten",
      SERVICES: "Zakelijke dienstverlening",
      HORECA: "Horeca",
      HEALTHCARE: "Zorg en welzijn",
      EDUCATION: "Onderwijs",
      MANUFACTURING: "Productie en maakindustrie",
      CONSTRUCTION: "Bouw en installatie",
      FINANCE: "Financiële dienstverlening",
      REAL_ESTATE: "Vastgoed",
      LEGAL: "Juridisch",
      MEDIA: "Media en uitgeverij",
      TRAVEL: "Reizen en toerisme",
      OTHER: "Overig",
    },
    permission: {
      PRIVATE: "Privé",
      READ: "Alleen lezen",
      "READ-ONLY": "Alleen lezen",
      ADVISE: "Adviseren",
      ACT: "Uitvoeren",
    },
    confidence_pill: {
      HIGH: "Hoog",
      MEDIUM: "Gemiddeld",
      LOW: "Laag",
    },
    tier_explain: {
      TIER_A: "komt uit een gemeten cijfer of een gekoppelde advertentie-/analytics-tool",
      TIER_B: "komt uit een onderbouwde externe bron of een berekend model",
      TIER_C: "algemene indicatie, niet direct gemeten in uw account",
      TIER_D: "richting-indicatie zonder harde meting",
    },
  },
  decisionTwin: {
    fallback_badge: "KLASSIEKE WEERGAVE",
    fallback_note: "Dit advies heeft nog geen uitgebreide gegevens — beperkt zichtbaar.",
    advice: "Advies",
    why: "Waarom",
    baseline: "Uitgangssituatie",
    baseline_missing: "Uitgangssituatie niet betrouwbaar in beeld",
    no_change: "Als u niets verandert",
    no_change_is_advice: "Dit is precies wat JustEasySEO adviseert — geen wijziging.",
    no_change_unknown: "Verwachting op basis van 30-daagse doortrekking ontbreekt.",
    follow_advice: "Als u het advies volgt",
    follow_missing: "Geen expliciet bereik beschikbaar",
    confidence: "Hoe zeker",
    risk: "Risico",
    expected_upside: "Verwachte verbetering",
    evidence_class: "Type bewijs",
    evidence_disclaimer: "Deze verwachting is {class} en is geen behaald resultaat.",
    reconsider_triggers: "Wat zou mijn advies veranderen?",
    reconsider_empty: "Geen expliciete voorwaarden — advies blijft geldig tot herbeoordeling op {date}.",
    review_at: "Herbeoordelen op",
    expires_at: "Verloopt op",
    last_revalidated_at: "Laatst herzien",
    reality_vs_nexora: "Werkelijkheid vs. JustEasySEO-pad",
    reality: "WERKELIJKHEID",
    nexora_path: "JustEasySEO-pad",
    user_action_taken: "Wat u deed",
    reality_after: "Waargenomen na besluit",
    reality_no_measurement: "Nog geen meting binnen het herbeoordelingsvenster.",
    comparison_unreliable: "Vergelijking niet betrouwbaar te maken — type bewijs is {class}.",
    what_did_you_do: "Wat heeft u gedaan?",
    action_followed: "Ik heb het advies gevolgd",
    action_partial: "Ik heb het advies gedeeltelijk gevolgd",
    action_different: "Ik heb iets anders gedaan",
    action_none: "Ik heb niets gewijzigd",
    action_pending: "Nog niet besloten",
    note_optional: "Optionele notitie",
    similar_decisions: "Vergelijkbare eerdere besluiten",
    similar_empty: "Nog geen vergelijkbare eerdere besluiten in dezelfde context.",
    reliability: "Betrouwbaarheid voor uw bedrijf",
  },
};

// Legacy widget compatibility aliases (kept for existing callers).
nl.dashboard.ask_ph = nl.dashboard.ask_ph;
nl.ledger = { ...nl.ledger };

// ---- Punt 1-6 additions -------------------------------------------------
nl.dashboard.marketing_score_label = "Marketingscore";
nl.dashboard.marketing_score_hint =
  "Meet hoe goed uw marketing loopt op basis van uitgaven, klikken, conversies en signalen uit uw Bedrijfskennis.";
nl.dashboard.marketing_score_not_ready = "Nog niet te bepalen";
nl.dashboard.marketing_score_add_data = "Voeg meer gegevens toe in Gegevens en koppelingen";

nl.brain.page_title = "Wat JustEasySEO over uw bedrijf weet";
nl.brain.tab_profile = "Uw bedrijf";
nl.brain.tab_knowledge = "Wat JustEasySEO over uw bedrijf weet";

// Concurrent-vergelijker
nl.comparison = {
  card_title: "Vergelijking met uw aanbod",
  toggle_open: "Vergelijk",
  toggle_close: "Sluit vergelijking",
  refresh: "Bijwerken",
  short_overview: "Kort overzicht",
  no_overlap: "Onvoldoende overlap gevonden voor een vergelijking op onderdelen.",
  no_snapshot: "Voor een vergelijking is een website-opname van deze concurrent nodig.",
  snapshot_cta: "Website nu vastleggen",
  profile_incomplete_prefix: "Vul eerst",
  profile_incomplete_suffix: "in uw bedrijfsprofiel aan om een goede vergelijking te maken.",
  goto_brain: "Naar Bedrijfskennis",
  generating: "JustEasySEO vergelijkt uw aanbod met deze concurrent…",
};
nl.brain.tab_competitors = "Uw concurrenten";

nl.vault.example_data_banner = "Voorbeeldgegevens";
nl.vault.example_data_hint = "U kijkt naar demo-gegevens — nog geen echte koppeling.";
nl.vault.really_linked = "Werkelijk gekoppeld";
nl.vault.read_only_pill = "Alleen lezen";
nl.vault.read_only_hint = "JustEasySEO leest gegevens, wijzigt niets.";
nl.vault.what_nexora_reads = "Wat JustEasySEO leest";
nl.vault.advanced_toggle = "Geavanceerde instellingen";
nl.vault.advanced_hide = "Verberg geavanceerde instellingen";

nl.intake.ask_website = "Wat is het adres van uw website?";
nl.intake.field_company_name = "Bedrijfsnaam";
nl.intake.field_website = "Website-adres";
nl.intake.nexora_reads = "JustEasySEO leest mee";
// M124-e: intake-validatie + fallback-weergave voor corrupte legacy waarden.
nl.intake.errors = {
  budget_negative: "Voer een bedrag van 0 of hoger in. Kies 0 als u geen advertentiebudget wilt inzetten.",
};
nl.intake.display = {
  budget_invalid: "Onbekend bedrag",
};
// M124-e: aanvullende B1-NL substituties voor labels die eerder Engels/tech waren.
nl.ui = nl.ui || {};
nl.ui.confidence_low_label = "Zekerheid: laag";
nl.ui.confidence_medium_label = "Zekerheid: middel";
nl.ui.confidence_high_label = "Zekerheid: hoog";
nl.ui.sandbox_label = "Testomgeving";
nl.ui.spend_unknown = "Nog onbekend";
nl.ui.spend_unknown_hint = "Nog niet gemeten — koppel Google Ads of Meta Ads om te zien.";

nl.weekly.kpi_cap_label = "Maandelijkse bovengrens";
nl.weekly.kpi_projection_label = "Verwachting einde van de maand";
nl.weekly.projection_note = "Berekening is een rechte lijn op basis van huidig tempo.";
nl.weekly.ghost_note = "Adviesmodus — JustEasySEO observeert en adviseert, geen automatische uitvoering.";

nl.auth.headline = "SEO zonder gedoe.";
nl.auth.subhead = "SEO, advertenties en social media. Simpel geregeld. JustEasySEO bekijkt uw marketing en laat zien waar kansen liggen. U beslist zelf wat u ermee doet.";
nl.auth.welcome_back = "Welkom terug.";
nl.auth.stat_google_ads = "Google Ads";
nl.auth.stat_meta = "Meta";
nl.auth.stat_tiktok = "TikTok";
nl.auth.stat_linkedin = "LinkedIn";
nl.auth.channel_status_demo = "Voorbeeldgegevens";
nl.auth.channel_status_not_connected = "Niet gekoppeld";
nl.auth.channel_status_read_only = "Alleen lezen — gekoppeld";
nl.auth.error_invalid = "De ingevoerde combinatie klopt niet. Controleer uw e-mailadres en wachtwoord.";
nl.auth.tech_code_prefix = "Technische code";

nl.wallet.not_set_short = "Nog niet ingesteld";
nl.wallet.set_cap_cta = "Stel uw maandbudget in";
nl.wallet.stat_cap = "Maandbudget";
nl.wallet.monthly_cap = "Maandbudget (€)";
nl.wallet.mode_fixed_hint = "De optelsom van uw kanaalbudgetten moet gelijk zijn aan uw maandbudget.";
nl.wallet.mode_flexible_hint = "De optelsom van uw kanaalbudgetten mag uw maandbudget niet overschrijden; het resterende deel is flexibel te besteden.";

// Competitor snapshots — plainer wording
nl.competitors.snapshots_run = "Website nu vastleggen";
nl.competitors.snapshots_running = "Website vastleggen…";
nl.competitors.snapshots_view = "Eerdere controles bekijken";
nl.competitors.snapshots_empty_no_website = "Voeg eerst het adres van de website van deze concurrent toe.";
nl.competitors.snapshots_edit_competitor = "Concurrent bewerken";
nl.competitors.snapshots_empty_no_snap = "Nog geen controles voor deze concurrent. Leg de website vast om te beginnen.";
nl.competitors.snapshots_empty_one_snap = "Er is één controle vastgelegd. Voor het tonen van veranderingen zijn twee controles nodig.";
nl.competitors.snapshots_empty_action_next = "Volgende controle vastleggen";
nl.competitors.snapshots_disabled_tooltip = "Eerst een website toevoegen";
nl.competitors.snapshots_back_to_brain = "Naar Bedrijfskennis";
nl.competitors.snapshots_back = "Terug naar Concurrenten volgen";
nl.competitors.snapshots_title = "{name} — websiteveranderingen";
nl.competitors.change_ok = "Controle uitgevoerd — {n} verandering(en) gevonden.";
nl.competitors.change_none = "Controle uitgevoerd — geen wijzigingen ten opzichte van de vorige controle.";
nl.competitors.confirm_real = "U staat op het punt de website {domain} vast te leggen. Dit stuurt een verzoek naar de openbare website. Doorgaan?";

nl.market.observation_prefix_strip = true;
nl.market.source_llm_general = "Algemene achtergrondkennis";
nl.market.source_llm_general_hint = "Niet gecontroleerd met actuele bronnen.";
nl.market.timeframe = {
  IMMEDIATE: "de komende weken",
  WEEKS: "de komende weken",
  QUARTERS: "de komende 6 maanden",
  NEXT_2_QUARTERS: "de komende twee kwartalen",
  YEAR_PLUS: "meer dan een jaar",
};

nl.weekly.empty_no_sources_headline = "Nog geen gekoppelde gegevens voor deze week.";
nl.weekly.empty_no_sources_hint = "Zodra u een bron koppelt in Gegevens en koppelingen, verschijnt hier uw eerste weekrapport.";

nl.dashboard.ghost_hypo_range_empty = "Nog niet te bepalen";
nl.dashboard.ghost_hypo_range_reason = "Nog geen gekoppelde gegevens om een schatting op te baseren.";
nl.dashboard.ghost_hypo_measured = "Gemeten resultaat";
nl.dashboard.ghost_hypo_estimate_label = "Mogelijk effect";

// Intake field labels (side panel)
nl.intake_field = {
  COMPANY_NAME: "Bedrijfsnaam",
  WEBSITE: "Website",
  INDUSTRY: "Branche of sector",
  BUSINESS_MODEL: "Soort klanten",
  PRODUCTS_SERVICES: "Uw producten of diensten",
  TARGET_AUDIENCE: "Uw doelgroep",
  IDEAL_CUSTOMER: "Uw ideale klant",
  UNWANTED_CUSTOMERS: "Klanten die u liever mijdt",
  REGIONS: "Regio's waar u werkt",
  CAPACITY_NEW_CUSTOMERS: "Nieuwe klanten per maand",
  SEASONS_NOTE: "Piek- of rustperiodes",
  GROWTH_GOALS: "Uw groeidoel",
  REVENUE_GOALS: "Uw omzetdoel",
  MARKETING_GOALS: "Uw marketingdoel",
  COMPETITORS_TEXT: "Uw concurrenten",
  BRAND_TONE: "Schrijfstijl",
  BRAND_STYLE: "Uw merkstijl",
  BRAND_VALUES: "Uw kernwaarden",
  MONTHLY_BUDGET: "Maandbudget voor marketing",
  RISK_APPETITE: "Hoe voorzichtig of ondernemend mag JustEasySEO adviseren?",
  COMPANY_SIZE: "Bedrijfsgrootte",
  MARGINS_NOTE: "Marge-indicatie",
};
nl.intake_field_value = {
  B2C: "Particulieren",
  B2B: "Bedrijven",
  B2B2C: "Via andere bedrijven aan particulieren",
  MIXED: "Bedrijven én particulieren",
  CONSERVATIVE: "Voorzichtig",
  BALANCED: "Middenweg",
  AGGRESSIVE: "Ondernemend",
  // Marketingdoel — display-map voor bestaande antwoorden. Sleutels
  // gebruiken uppercase van de eerder opgeslagen chip-string (incl.
  // spaties) zodat oude gegevens correct renderen. Nieuwe intakes
  // schrijven direct de NL-formulering (chips in intake_script.py).
  "MEER LEADS": "Meer aanvragen",
  "LAGERE CAC": "Lagere kosten per nieuwe klant",
  "BETERE ROAS": "Beter rendement op advertentie-uitgaven",
  "GROTE ENTERPRISE TRAJECTEN": "Grote zakelijke opdrachten",
};

// M123 — publish errors: twee varianten (JustEasySEO-eigen limiet vs LinkedIn-limiet)
nl.publish = nl.publish || {};
nl.publish.errors = {
  video_too_large_nexora:
    "Deze video is groter dan onze limiet van {mb} MB.",
  video_too_large_linkedin:
    "Deze video is te groot voor LinkedIn — maximaal {mb} MB toegestaan.",
  video_probe_unavailable:
    "We kunnen deze video nu niet controleren. Probeer het later opnieuw.",
  image_too_large_nexora:
    "Deze afbeelding is groter dan onze limiet van {mb} MB.",
  image_pixels_too_many:
    "Afbeelding heeft meer dan {pixels} pixels — dit is de LinkedIn-grens.",
  gif_frames_too_many:
    "GIF heeft meer dan {frames} frames — dit is de LinkedIn-grens.",
  mime_mismatch:
    "Bestandstype komt niet overeen met de inhoud.",
  upload_target_untrusted:
    "Upload-doel niet vertrouwd. Actie geweigerd.",
  upload_target_redirect:
    "Upload-doel stuurde ons door naar een andere host.",
  upload_url_expired:
    "Upload-doel is verlopen. Probeer het opnieuw.",
  video_still_processing:
    "Video staat bij LinkedIn nog in verwerking. We proberen het straks opnieuw.",
  needs_reapproval:
    "Deze inhoud is aangepast na goedkeuring. Keur het voorstel opnieuw goed voordat u publiceert.",
  sent_unknown:
    "Het bericht is verstuurd maar we konden de bevestiging niet ophalen. Controleer het handmatig voordat u opnieuw plaatst.",
};

nl.publish.errors.tiktok_inbox_pending =
  "Video staat klaar in uw TikTok-app om te bevestigen en te plaatsen.";
nl.publish.errors.tiktok_privacy_option_unavailable =
  "Deze zichtbaarheidsoptie is niet meer beschikbaar voor uw TikTok-account. Kies een andere en dien opnieuw in ter goedkeuring.";
nl.publish.errors.tiktok_private_result =
  "Geplaatst als privé of beperkt zichtbaar op TikTok.";
nl.publish.errors.meta_sent_unknown =
  "Het bericht is verstuurd maar we konden de bevestiging niet ophalen. Controleer het handmatig voordat u opnieuw plaatst.";
nl.publish.errors.ig_processing =
  "Instagram verwerkt uw bericht";
nl.publish.errors.ig_ready_to_publish =
  "Klaar om te plaatsen op Instagram";
nl.publish.errors.ig_published =
  "Geplaatst op Instagram";
nl.publish.errors.ig_container_error =
  "Instagram meldde een fout tijdens verwerken.";
nl.publish.errors.ig_container_expired =
  "Instagram-container is verlopen.";
nl.publish.errors.fb_video_processing =
  "Uw video wordt verwerkt door Facebook.";
nl.publish.errors.fb_video_ready =
  "Video geplaatst op Facebook.";
nl.publish.errors.fb_video_failed =
  "Facebook kon deze video niet plaatsen.";

// Data-category labels for Gegevens en koppelingen
nl.vault.data_category = {
  PUBLIC_WEBSITE_CONTENT: "Openbare website-inhoud",
  MARKETING_METRICS: "Marketingcijfers",
  AGGREGATED_CUSTOMER_METRICS: "Geanonimiseerde klantcijfers",
  CUSTOMER_PII: "Persoonsgegevens van klanten",
  PAYMENT_DATA: "Betaalgegevens",
  ANALYTICS_DATA: "Analytics-cijfers",
  AD_PERFORMANCE: "Advertentie-resultaten",
  SEARCH_ANALYTICS: "Zoekresultaten uit Google",
  SOCIAL_CONTENT: "Openbare sociale posts",
};

export default nl;
